import { autopricing, pollAsyncRequestResult } from '~/api/projects';
import { Button } from '~/components';
import { useAutoSaveContext } from '~/hooks';
import { DeploymentTypes } from '~/constants/deployment-type';
import { Popup } from 'semantic-ui-react';
import { Project } from '~/types/project';
import toast from 'react-hot-toast';
import AsyncRequestButton from '~/components/AsyncRequestButton';
import useArchiveToast from '~/hooks/archive/useArchiveToast';
import { ToastDuration } from '~/constants/toasts';
import { usePermissions } from '~/requests/permissions/usePermissions';


interface PricingEstimateButtonProps {
  project: Project;
  projectState: any;
  errors: any;
}

export default function PricingEstimateButton({ 
  project, 
  projectState, 
  errors 
}: PricingEstimateButtonProps) {
  const { permissions } = usePermissions();
  const hasPricingEstimate = Boolean(project.priceEstimateTimestamp);  
  const { fetchFreshData } = useAutoSaveContext() as any;
  const { showArchiveToast } = useArchiveToast();
  const showPricingEstimate = !projectState?.isITC && 
    !project.hasStorage && 
    project?.deploymentType !== DeploymentTypes.COMMUNITY_SOLAR && 
    !projectState?.isEpcOperating;
  const projectArrayKeys = Object.keys(errors).filter((key) => key.includes('projectArrays'));
  const hasProjectArrayErrors = Boolean(projectArrayKeys.length);

  if (showPricingEstimate || permissions.isAdmin) {
    return (
      <div style={{ display: 'flex', flexDirection: 'row', alignItems: 'center', gap: 'var(--x-small)' }}>
        <AsyncRequestButton
          apiEvent={project.apiEvents?.autopricing}
          onClick={async () => {
            if (showArchiveToast()) {
              return;
            }

            try {
              return await autopricing(project.id);
            } catch (err) {
              toast.error('Error initializing pricing estimate request');
              throw err;
            }
          }}
          disabled={errors.addressState || errors.epcCost || hasProjectArrayErrors}
          onSuccess={() => {
            fetchFreshData().catch((err: any) => {
              console.error('Error fetching fresh data after making pricing estimate request', err);
              toast.error('An unexpected error occurred while fetching project data after finishing the pricing estimate request. Please refresh the page.', { duration: ToastDuration.LONG });
            });
          }}
          onError={(errorObj: any) => {
            if (errorObj?.response?.data?.outOfBounds) {
              toast.error((
                <div>
                  <p>
                    Conductor Solar was unable to find a workable price range for your project. Please review your project details,
                    paying specific attention to the project size and overall costs.
                  </p>
                  <p>
                    If you believe this is in error, please contact{' '}
                    <a href="mailto:support@conductor.solar">support@conductor.solar</a> for assistance.
                  </p>
                </div>
              ), {
                duration: ToastDuration.LONG
              });
            } else if (errorObj?.response?.data?.errorInIRR) {
              toast.error((
                <div>
                  <p>
                    Conductor Solar is having difficulty generating a price, and this is likely due to choosing the wrong unit selector for one of your inputs (e.g., year 1 production).
                  </p>
                  <p>
                    After you&apos;ve confirmed your inputs, please contact{' '}
                    <a href="mailto:support@conductor.solar">support@conductor.solar</a> for assistance.
                  </p>
                </div>
              ), { 
                duration: ToastDuration.LONG 
              });
            } else {
              toast.error('An error occurred fetching pricing estimate data. Please refresh the page and try again.');
              fetchFreshData();
            }
          }}
          onTimeout={() => toast.error('Pricing estimates are taking longer than expected. Please refresh the page to check for results.')}
          text={hasPricingEstimate ? 'Update pricing estimate' : 'Generate pricing estimate'}
          loadingText={hasPricingEstimate ? 'Updating pricing estimate' : 'Generating pricing estimate'}
          pollFn={(apiEventId: string) => pollAsyncRequestResult(project.id, apiEventId)}
        />
        
      </div>
    );
  } else {
      let disabledInstanceText = null;
      if (project?.deploymentType === DeploymentTypes.COMMUNITY_SOLAR) {
        disabledInstanceText = 'Pricing estimates are not available at this time for community solar projects.';
      } else if (projectState?.isITC) {
        disabledInstanceText = 'Pricing estimates are not available at this time for ITC transfers.';
      } else if (project?.hasStorage) {
        disabledInstanceText = 'Pricing estimates are not available at this time for projects that include BESS.';
      } else if (projectState?.isEpcOperating) {
        disabledInstanceText = 'Pricing estimates are not available at this time for operating projects.';
      } else {
        disabledInstanceText = 'Pricing estimates are not available for this project.';
      }
    
      return (
        <div style={{ display: 'flex', flexDirection: 'row', alignItems: 'center', gap: 'var(--x-small)' }}>
          <Popup
            inverted
            position="left center"
            content={disabledInstanceText}
            on={['hover', 'focus']}
            trigger={
              <span style={{ display: 'inline-block' }} tabIndex={0}>
                <Button outlined disabled>
                  {hasPricingEstimate ? 'Update pricing estimate' : 'Generate pricing estimate'}
                </Button>
              </span>
            }
          />
        </div>
      );
    }
}

    